import { Navigate, type RouteObject } from "react-router";

import CenterAdminRouteGuard from "./ui/components/CenterAdminRouteGuard";
import CenterAdminPage from "./ui/pages/CenterAdminPage";
import CenterAgendaPage from "./ui/pages/CenterAgendaPage";
import CenterCoursesPage from "./ui/pages/CenterCoursesPage";
import CenterJoinPage from "./ui/pages/CenterJoinPage";
import CenterMemberDeparturePage from "./ui/pages/CenterMemberDeparturePage";
import CenterPatientsPage from "./ui/pages/CenterPatientsPage";
import CenterCourseReportPage from "./ui/pages/CenterCourseReportPage";
import CenterPractitionerReportPage from "./ui/pages/CenterPractitionerReportPage";
import CenterStudentReportPage from "./ui/pages/CenterStudentReportPage";
import CenterPaymentsPage from "./ui/pages/CenterPaymentsPage";
import CenterSubscriptionPage from "./ui/pages/CenterSubscriptionPage";
import CenterReportsPage from "./ui/pages/CenterReportsPage";
import CenterSignUpPage from "./ui/pages/CenterSignUpPage";
import CenterTeamPage from "./ui/pages/CenterTeamPage";
import CenterToolsPage from "./ui/pages/CenterToolsPage";

/** Public: signing a centre up, and opening a join link. */
export const centerPublicRoutes: RouteObject[] = [
    { path: "/signup/center", element: <CenterSignUpPage /> },
    // Public on purpose: the person holding a join link may have no account
    // yet, and sending them to a login wall first loses the invitation.
    { path: "/center-invite/:token", element: <CenterJoinPage /> },
];

/**
 * The administration surface, behind the admin guard. Nested so every screen
 * added later inherits the same refusal without remembering to ask for it.
 */
export const centerAdminRoutes: RouteObject[] = [
    {
        element: <CenterAdminRouteGuard />,
        children: [
            { path: "/center", element: <CenterAdminPage /> },
            { path: "/center/courses", element: <CenterCoursesPage /> },
            { path: "/center/patients", element: <CenterPatientsPage /> },
            // The fields moved under the patient list; an old link lands there.
            { path: "/center/fields", element: <Navigate to="/center#patient-fields" replace /> },
            { path: "/center/agenda", element: <CenterAgendaPage /> },
            { path: "/center/reports", element: <CenterReportsPage /> },
            {
                path: "/center/reports/practitioners/:therapistId",
                element: <CenterPractitionerReportPage />,
            },
            { path: "/center/reports/students/:kind/:subjectId", element: <CenterStudentReportPage /> },
            { path: "/center/reports/courses/:courseId", element: <CenterCourseReportPage /> },
            { path: "/center/payments", element: <CenterPaymentsPage /> },
            { path: "/center/subscription", element: <CenterSubscriptionPage /> },
            { path: "/center/tools", element: <CenterToolsPage /> },
            { path: "/center/settings", element: <Navigate to="/center" replace /> },
            { path: "/center/team", element: <CenterTeamPage /> },
            { path: "/center/team/:membershipId/leave", element: <CenterMemberDeparturePage /> },
            {
                path: "/center/team/:membershipId/stop-practising",
                element: <CenterMemberDeparturePage mode="stopPractising" />,
            },
        ],
    },
];

/**
 * A therapist's own centre business, with no administration in it.
 *
 * It lives in the profile's Centre tab now, beside the centre's details. The
 * old address still leads there, so a link in an email or a bookmark is not a
 * dead end.
 */
export const centerMemberRoutes: RouteObject[] = [
    { path: "/my-center/consent", element: <Navigate to="/profile?tab=center" replace /> },
];
